import React, { useState, useEffect } from "react";
import { Card, Row, Col, Button, Empty, Spin, Typography, Result, Dropdown } from "antd";
import {
  LeftOutlined,
  LockOutlined,
  PlusOutlined,
  MinusOutlined,
  DownOutlined
} from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useToken from "../Authentication/useToken";
import Constants from "../Utils/Constants";
import {
  exportMatchPdf,
  exportPacePdf,
  exportPathPdf,
} from "./ExportEvaluationPdf";
import { exportFollowUpPacePdf } from "./ExportFollowUpPdf";
import { exportAllFollowUpsPaceCsv, exportFollowUpPaceCsv } from "./ExportFollowUpCsv";

const { Title, Text } = Typography;

function EvaluationDisplay() {
  const { t } = useTranslation(["Evaluations", "FollowUpCall"]);
  const { token } = useToken(); // Récupération du token d'authentification
  const [loadingEvaluations, setLoadingEvaluations] = useState(false); // Indicateur de chargement evals
  const [loadingFollowUps, setLoadingFollowUps] = useState(false); // Indicateur de chargement suivis
  const [errorMessageEvaluations, setErrorMessageEvaluations] = useState(""); // Message d'erreur evaluations
  const [errorMessageFollowUps, setErrorMessageFollowUps] = useState("");
  const [evaluations, setEvaluations] = useState([]); // Liste des évaluations récupérées
  const [followUps, setFollowUps] = useState([]); // Liste des appels de suivis
  const [patient, setPatient] = useState(null); // Informations du patient
  const navigate = useNavigate();
  const { patientId } = useParams(); // Récupère l'ID du patient depuis l'URL
  const [expandedEvaluations, setExpandedEvaluations] = useState(new Set()); // Pour déplier plusieurs evaluations en meme temps
  const [expandedFollowUps, setExpandedFollowUps] = useState(new Set()); // Pour déplier plusieurs follow-ups en meme temps

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
  if (token && patientId) {
    console.log("TOKEN utilisé pour patient :", token);
    fetchDataEvaluations();
    fetchDataFollowUp();
  }
  // Ajoute `token` ici sinon React rouspète (et tu évites les updates infinis)
}, [token, patientId]); // eslint-disable-line react-hooks/exhaustive-deps




  const fetchDataEvaluations = async () => {
    setLoadingEvaluations(true);
    setErrorMessageEvaluations("");

    try {
      // Récupération des informations du patient si un ID est fourni
      if (patientId) {
        try {
          console.log(
            `Récupération du patient: ${Constants.SERVER_URL}/patient/${patientId}`
          );
          const response = await fetch(
            `${Constants.SERVER_URL}/patient/${patientId}`,
            {
              headers: { Authorization: "Bearer " + token },
            }
          );

          if (!response.ok) {
            console.error(
              `Erreur patient: ${response.status} ${response.statusText}`
            );
            throw new Error(
              `Erreur ${response.status}: ${response.statusText}`
            );
          }

          const patientData = await response.json();
          if (!patient || patient.id !== patientData.id) {
          setPatient(patientData);
          }
          console.log("Patient récupéré:", patientData);
        } catch (patientError) {
          console.error("Erreur détaillée (patient):", patientError);
          // On continue même si la récupération du patient échoue
        }
      }

      // Récupération des évaluations
      const endpoint = patientId
        ? `evaluations/patient/${patientId}`
        : "evaluations";

      console.log(
        `Récupération des évaluations: ${Constants.SERVER_URL}/${endpoint}`
      );

      const evaluationsResponse = await fetch(
        `${Constants.SERVER_URL}/${endpoint}`,
        {
          headers: { Authorization: "Bearer " + token },
        }
      );

      if (!evaluationsResponse.ok) {
        console.error(
          `Erreur évaluations: ${evaluationsResponse.status} ${evaluationsResponse.statusText}`
        );
        throw new Error(
          `Erreur ${evaluationsResponse.status}: ${evaluationsResponse.statusText}`
        );
      }

      // Récupérer le texte brut pour le déboguer avant de le parser
      const responseText = await evaluationsResponse.text();
      console.log("Réponse brute:", responseText);

      // Vérifier si la réponse est vide ou non JSON
      if (!responseText || responseText.trim() === "") {
        throw new Error("La réponse du serveur est vide");
      }

      let evaluationsData;
      try {
        evaluationsData = JSON.parse(responseText);
      } catch (jsonError) {
        console.error("Erreur de parsing JSON:", jsonError);
        console.error("Contenu reçu:", responseText);
        throw new Error(`Erreur de format JSON: ${jsonError.message}`);
      }

      console.log("Évaluations récupérées:", evaluationsData);

      if (Array.isArray(evaluationsData)) {
        const sortedEvaluations = evaluationsData.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        setEvaluations(sortedEvaluations);
      } else {
        console.error(
          "Les données d'évaluations ne sont pas un tableau:",
          evaluationsData
        );
        setErrorMessageEvaluations("Format de données incorrect pour les évaluations");
      }
    } catch (error) {
      console.error("Erreur détaillée:", error);
      setErrorMessageEvaluations(
        `Erreur: ${
          error.message ||
          "Une erreur est survenue lors du chargement des données."
        }`
      );
    } finally {
      setLoadingEvaluations(false);
    }
  };

  const fetchDataFollowUp = async () => {
    setLoadingFollowUps(true);
    setErrorMessageFollowUps("");

    try {
      const endpoint = patientId
        ? `follow-up/follow-up-calls-pace/patient/${patientId}`
        : "follow-up-calls-pace";

      console.log(
        `Récupération des appels de suivi: ${Constants.SERVER_URL}/${endpoint}`
      );

      const followUpsResponse = await fetch(
        `${Constants.SERVER_URL}/${endpoint}`,
        {
          headers: { Authorization: "Bearer " + token },
        }
      );

      if (!followUpsResponse.ok) {
        console.error(
          `Erreur appels de suivi: ${followUpsResponse.status} ${followUpsResponse.statusText}`
        );
        throw new Error(
          `Erreur ${followUpsResponse.status}: ${followUpsResponse.statusText}`
        );
      }

      // Récupérer le texte brut pour le déboguer avant de le parser
      const responseText = await followUpsResponse.text();
      console.log("Réponse brute:", responseText);

      // Vérifier si la réponse est vide ou non JSON
      if (!responseText || responseText.trim() === "") {
        throw new Error("La réponse du serveur est vide");
      }

      let followUpData;
      try {
        followUpData = JSON.parse(responseText);
      } catch (jsonError) {
        console.error("Erreur de parsing JSON:", jsonError);
        console.error("Contenu reçu:", responseText);
        throw new Error(`Erreur de format JSON: ${jsonError.message}`);
      }

      console.log("Appels de suivis récupérées:", followUpData);

      if (Array.isArray(followUpData)) {
        const sortedFollowUps = followUpData.sort(
          (a, b) => new Date(b.currentDate) - new Date(a.currentDate)
        );
        setFollowUps(sortedFollowUps);
        console.log(sortedFollowUps);
      } else {
        console.error(
          "Les données de l'appel de suivi ne sont pas un tableau:",
          followUpData
        );
        setErrorMessageFollowUps("Format de données incorrect pour les appels de suivis");
      }
    } catch (error) {
      console.error("Erreur détaillée:", error);
      setErrorMessageFollowUps(
        `Erreur: ${
          error.message ||
          "Une erreur est survenue lors du chargement des données."
        }`
      );
    } finally {
      setLoadingFollowUps(false);
    }
  };

  const handleReturn = () => {
    navigate(-1);
  };

  const handleExportPdf = async (evaluation, programmeRecommande) => {
    let matchPath = null;
    if (evaluation.Evaluation_MATCH) matchPath = evaluation.Evaluation_MATCH;
    if (evaluation.Evaluation_PATH) matchPath = evaluation.Evaluation_PATH;

    if (matchPath) {
      const payload = {
        date: matchPath.createdAt.split("T")[0],
        idPatient: patientId,
        chairTestSupport:
          matchPath.chairTestSupport === "true" ? "without" : "with",
        chairTestCount: matchPath.chairTestCount,
        balanceFeetTogether: matchPath.BalanceFeetTogether,
        balanceSemiTandem: matchPath.balanceSemiTandem,
        balanceTandem: matchPath.balanceTandem,
        walkingTime:
          matchPath.vitesseDeMarche !== 0
            ? Math.round((4 / matchPath.vitesseDeMarche) * 100) / 100
            : 0,
        scores: {
          cardioMusculaire: matchPath.scoreCM,
          equilibre: matchPath.scoreBalance,
          total: matchPath.scoreTotal,
          program: programmeRecommande,
        },
      };
      if (evaluation.Evaluation_MATCH) await exportMatchPdf(payload, token);
      if (evaluation.Evaluation_PATH) await exportPathPdf(payload, token);
    }

    if (evaluation.Evaluation_PACE) {
      const pace = evaluation.Evaluation_PACE;
      const payload = {
        date: pace.createdAt.split("T")[0],
        idPatient: patientId,
        chairTestSupport: pace.chairTestSupport === "true" ? "without" : "with",
        chairTestCount: pace.chairTestCount,
        balanceFeetTogether: pace.BalanceFeetTogether,
        balanceSemiTandem: pace.balanceSemiTandem,
        balanceTandem: pace.balanceTandem,
        balanceOneFooted: pace.balanceOneFooted,
        frtSitting: pace.frtSitting === "true" ? "sitting" : "standing",
        frtDistance: pace.frtDistance,
        walkingTime:
          pace.vitesseDeMarche !== 0
            ? Math.round((4 / pace.vitesseDeMarche) * 100) / 100
            : 0,
        scores: {
          cardioMusculaire: pace.scoreA,
          equilibre: pace.scoreB,
          mobilite: pace.scoreC,
          color: programmeRecommande.split(" ")[0],
          level: programmeRecommande.split(" ")[1],
          program: programmeRecommande,
        },
      };
      exportPacePdf(payload, token);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = date.getDate();

    // Liste des mois en français
    const months = [
      "janvier",
      "février",
      "mars",
      "avril",
      "mai",
      "juin",
      "juillet",
      "août",
      "septembre",
      "octobre",
      "novembre",
      "décembre",
    ];
    const month = months[date.getMonth()];
    const year = date.getFullYear();

    return `${day} ${month} ${year}`;
  };

  const toggleEvaluation = (evaluationId) => {
    setExpandedEvaluations((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(evaluationId)) {
        newSet.delete(evaluationId);
      } else {
        newSet.add(evaluationId);
      }
      return newSet;
    });
  };

  const renderEvaluation = (evaluation, index) => {
    // Déterminer le type d'évaluation et extraire les données
    let type = "Inconnu";
    let cardioData = {};
    let equilibreData = {};
    let mobiliteData = {};
    let scoreTotal = "";
    let programmeRecommande = "";
    let vitesseMarche = "";
    let programmeDebut = "";
    let programmeFin = "";
    let programmeDuree = "";
    let programmeFrequence = "";
    let programmeModalite = "";
if (evaluation.Evaluation_PACE) {
  const pace = evaluation.Evaluation_PACE;
  const planning = evaluation.programPlanning || {};
  programmeModalite = planning.modality || "";
  type = "PACE";
  scoreTotal = `${pace.scoreTotal}/18`;
  programmeRecommande = evaluation.Program?.name || "";
  vitesseMarche = pace.vitesseDeMarche?.toFixed(2) || "0.00";

  programmeDebut = planning.programStartDate
    ? formatDate(planning.programStartDate)
    : "";
  console.log("Planning trouvé pour évaluation :", planning);

  programmeDuree =
  planning.programDurationWeeks != null
    ? `${planning.programDurationWeeks} semaines`
    : "Non spécifiée";


  if (planning.frequencyType === "auto") {
    programmeFrequence = "1 séance par jour (lundi à vendredi)";
  } else if (
    planning.frequencyType === "custom" &&
    Array.isArray(planning.customDays)
  ) {
    const joursFr = {
      monday: "Lundi",
      tuesday: "Mardi",
      wednesday: "Mercredi",
      thursday: "Jeudi",
      friday: "Vendredi",
      saturday: "Samedi",
      sunday: "Dimanche",
    };
    programmeFrequence = planning.customDays
      .map((day) => joursFr[day] || day)
      .join(", ");
  }

  if (planning.programStartDate && planning.programDurationWeeks != null) {
    const startDate = new Date(planning.programStartDate);
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + planning.programDurationWeeks * 7);
    programmeFin = formatDate(endDate);
  }

  cardioData = {
    support: pace.chairTestSupport ? "Avec appui" : "Sans appui",
    count: pace.chairTestCount || 0,
  };

  if (pace.balanceOneFooted > 0) {
    equilibreData = {
      test: "Unipodal",
      time: pace.balanceOneFooted,
    };
  } else if (pace.balanceTandem > 0) {
    equilibreData = {
      test: "Tandem",
      time: pace.balanceTandem,
    };
  } else if (pace.balanceSemiTandem > 0) {
    equilibreData = {
      test: "Semi-Tandem",
      time: pace.balanceSemiTandem,
    };
  } else {
    equilibreData = {
      test: "Pieds joints",
      time: pace.BalanceFeetTogether || 0,
    };
  }

  mobiliteData = {
    position: pace.frtSitting ? "Assis" : "Debout",
    distance: pace.frtDistance || 0,
  };

    } else if (evaluation.Evaluation_PATH) {
      const path = evaluation.Evaluation_PATH;
      type = "PATH";
      scoreTotal = path.scoreTotal;
      programmeRecommande = evaluation.Program?.name || "";
      vitesseMarche = path.vitesseDeMarche?.toFixed(2) || "0.00";

      cardioData = {
        support: path.chairTestSupport ? "Avec appui" : "Sans appui",
        count: path.chairTestCount || 0,
      };

      if (path.balanceTandem > 0) {
        equilibreData = {
          test: "Tandem",
          time: path.balanceTandem,
        };
      } else if (path.balanceSemiTandem > 0) {
        equilibreData = {
          test: "Semi-Tandem",
          time: path.balanceSemiTandem,
        };
      } else {
        equilibreData = {
          test: "Pieds joints",
          time: path.BalanceFeetTogether || 0,
        };
      }
    } else if (evaluation.Evaluation_MATCH) {
      const match = evaluation.Evaluation_MATCH;
      type = "MATCH";
      scoreTotal = `${match.scoreTotal}/9`;
      programmeRecommande = evaluation.Program?.name || "";
      vitesseMarche = match.vitesseDeMarche?.toFixed(2) || "0.00";

      cardioData = {
        support: match.chairTestSupport ? "Avec appui" : "Sans appui",
        count: match.chairTestCount || 0,
      };

      if (match.balanceTandem > 0) {
        equilibreData = {
          test: "Tandem",
          time: match.balanceTandem,
        };
      } else if (match.balanceSemiTandem > 0) {
        equilibreData = {
          test: "Semi-Tandem",
          time: match.balanceSemiTandem,
        };
      } else {
        equilibreData = {
          test: "Pieds joints",
          time: match.BalanceFeetTogether || 0,
        };
      }
    }

    const isExpanded = expandedEvaluations.has(evaluation.id);
    const evaluationNumber = evaluations.length - index;

    return (
      <div
        key={evaluation.id}
        style={{
          marginBottom: 20,
          borderRadius: 8,
          backgroundColor: "white",
          padding: "20px",
          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
        }}
      >
        <Row align="middle" justify="space-between">
          <Col span={8}>
            <Title level={4} style={{ margin: 0 }}>
              Évaluation {evaluationNumber} :
            </Title>
          </Col>

          <Col span={8}>
            <Text strong style={{ fontSize: "16px" }}>
              Date : {formatDate(evaluation.createdAt)}
            </Text>
          </Col>

          <Col span={4}>
            <Text strong style={{ fontSize: "16px" }}>
              Type : {type}
            </Text>
          </Col>

          <Col span={4} style={{ textAlign: "right" }}>
            <Button
              icon={<LockOutlined />}
              style={{ marginRight: "8px" }}
              onClick={() => handleExportPdf(evaluation, programmeRecommande)}
            >
              Télécharger PDF
            </Button>
            <Button
  icon={isExpanded ? <MinusOutlined /> : <PlusOutlined />}
  type="primary"
  style={{ backgroundColor: "#1890ff" }}
  onClick={() => toggleEvaluation(evaluation.id)}
  data-testid={`toggle-eval-${evaluation.id}`}
/>

       </Col>
</Row>

{isExpanded && (
  <div style={{ marginTop: 20 }} data-testid={`details-evaluation-${evaluation.id}`}>

    <Row gutter={[24, 16]}>
      <Col span={8}>
        <div>Score total : {scoreTotal}</div>
        <div>Programme recommandé : {programmeRecommande}</div>
        <div>Vitesse de marche (m/s) : {vitesseMarche}</div>
      </Col>

      <Col span={8}>
        <div>
          <strong>Cardio-musculaire :</strong>
          <div>{cardioData.support}</div>
          <div>Nombre de levers : {cardioData.count}</div>
        </div>
      </Col>

      <Col span={8}>
        <div>
          <strong>Équilibre :</strong>
          <div>Dernier test effectué : {equilibreData.test}</div>
          <div>Temps (seconde) : {equilibreData.time}</div>
        </div>
      </Col>

      {type === "PACE" && (
        <>
          <Col span={8} offset={8}>
            <div>
              <strong>Mobilité :</strong>
              <div>{mobiliteData.position}</div>
              <div>Distance (cm) : {mobiliteData.distance}</div>
            </div>
          </Col>

          <Col span={24} style={{ marginTop: 20 }}>
            <div>
              <strong>Date de début du programme :</strong> {programmeDebut}
            </div>
            <div>
              <strong>Durée du programme :</strong> {programmeDuree}
            </div>
            <div>
              <strong>Date de fin du programme :</strong> {programmeFin}
            </div>
            <div>
              <strong>Fréquence :</strong> {programmeFrequence}
            </div>
            <div>
            <strong>Modalité :</strong> {programmeModalite}
            </div>

          </Col>
        </>
      )}
    </Row>
  </div>
)}
</div>

       
      
    );
  };

  const toggleFollowUp = (followUpId) => {
    setExpandedFollowUps((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(followUpId)) {
        newSet.delete(followUpId);
      } else {
        newSet.add(followUpId);
      }
      return newSet;
    });
  };

  const formatFollowUpValue = (key, value) => {
    if (value === true) return t("FollowUpCall:label_yes");
    if (value === false) return t("FollowUpCall:label_no");
    if (value === null || value === undefined || value === "") return "—";

    const valueMap = {
      "s1": "S1", "s3": "S3", "s6": "S6", "s9": "S9", "s12": "S12",
      "never": t("FollowUpCall:label_never"),
      "less-than-once": t("FollowUpCall:label_less_than_once_a_week"),
      "once": t("FollowUpCall:label_once_a_week"),
      "twice": t("FollowUpCall:label_twice_a_week"),
      "three": t("FollowUpCall:label_three_a_week"),
      "four": t("FollowUpCall:label_four_a_week"),
      "five": t("FollowUpCall:label_five_a_week"),
      "six": t("FollowUpCall:label_six_a_week"),
      "seven": t("FollowUpCall:label_seven_a_week"),
      "alone": t("FollowUpCall:label_alone"),
      "with-caregiver": t("FollowUpCall:label_with_caregiver"),
      "caregiver-supervision": t("FollowUpCall:label_supervision_caregiver"),
      "with-friends": t("FollowUpCall:label_with_friends"),
      "very-easy": t("FollowUpCall:label_very_easy"),
      "easy": t("FollowUpCall:label_easy"),
      "hard": t("FollowUpCall:label_hard"),
      "very-hard": t("FollowUpCall:label_very_hard"),
      "no-pain": t("FollowUpCall:label_no_pain"),
      "light-pain": t("FollowUpCall:label_light_pain"),
      "moderate-pain": t("FollowUpCall:label_moderate_pain"),
      "severe-pain": t("FollowUpCall:label_severe_pain"),
      "sometimes": t("FollowUpCall:label_sometimes"),
      "often": t("FollowUpCall:label_often"),
      "always": t("FollowUpCall:label_always"),
      "very-unsatisfied": t("FollowUpCall:label_very_unsatisfied"),
      "unsatisfied": t("FollowUpCall:label_unsatisfied"),
      "satisfied": t("FollowUpCall:label_satisfied"),
      "very-satisfied": t("FollowUpCall:label_very_satisfied"),
      "doesnt-do": t("FollowUpCall:label_doesnt_do_exercise"),
      "level-1": t("FollowUpCall:label_difficulty_1"),
      "level-2": t("FollowUpCall:label_difficulty_2"),
      "level-3": t("FollowUpCall:label_difficulty_3"),
      "effort-linked": t("FollowUpCall:label_effort_linked"),
      "arthritis": t("FollowUpCall:label_arthritis"),
      "tendinopathy": t("FollowUpCall:label_tendinopathy"),
      "algic": t("FollowUpCall:label_algic"),
      "secondary-effect": t("FollowUpCall:label_secondary_effect"),
      "other": t("FollowUpCall:label_other"),
      "can-talk-easily": t("FollowUpCall:label_can_talk_easily"),
      "difficulty-talking": t("FollowUpCall:label_difficulty_talking"),
      "cant-talk": t("FollowUpCall:label_cant_talk"),
    };
    return valueMap[value] || String(value);
  };

  const renderDetailRow = (label, value) => {
    if (value === null || value === undefined || value === "") return null;
    return (
      <div style={{ marginBottom: 4 }}>
        <Text strong>{label} : </Text>
        <Text>{formatFollowUpValue(label, value)}</Text>
      </div>
    );
  };

  const followUpTypes = ["s1", "s3", "s6", "s9", "s12"];
  const followUpTypeLabels = { s1: "S1", s3: "S3", s6: "S6", s9: "S9", s12: "S12" };

  const getFollowUpByType = (type) => {
    return followUps.find((f) => f.evaluationType === type) || null;
  };

  const hasCompletedFollowUps = followUps.length > 0;

  const downloadOptions = [
    {
      label: "PDF",
      key: "pdf"
    },
    {
      label: "CSV",
      key: "csv"
    }
  ];

  const renderFollowUpRow = (type) => {
    const followUp = getFollowUpByType(type);
    const isCompleted = !!followUp;
    const isExpanded = expandedFollowUps.has(type);
    const levelMap = { 1: "I", 2: "II", 3: "III", 4: "IV", 5: "V" };

    return (
      <div
        key={type}
        style={{
          marginBottom: 20,
          borderRadius: 8,
          backgroundColor: "white",
          padding: "20px",
          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
        }}
      >
        <Row align="middle" justify="space-between">
          <Col span={5}>
            <Title level={4} style={{ margin: 0 }}>
              Appel de suivi {followUpTypeLabels[type]}
            </Title>
          </Col>
          <Col span={4}>
            <Text
              strong
              style={{
                fontSize: "14px",
                color: isCompleted ? "#52c41a" : "#ff4d4f",
              }}
            >
              {isCompleted ? "Complété" : "Non Complété"}
            </Text>
          </Col>
          <Col span={5}>
            <Text strong style={{ fontSize: "16px" }}>
              Date : {isCompleted ? formatDate(followUp.currentDate) : "NA"}
            </Text>
          </Col>
          <Col span={3}>
            <Text strong style={{ fontSize: "16px" }}>
              Type : PACE
            </Text>
          </Col>
          <Col span={7} style={{ textAlign: "right" }}>
            <Dropdown
              menu={{
                items: downloadOptions,
                onClick: (e) => {
                  if (e.key === "pdf" && isCompleted) {
                    exportFollowUpPacePdf(followUp, patientId);
                  } else if (e.key === "csv" && isCompleted) {
                    exportFollowUpPaceCsv(followUp, patientId);
                  }
                }
              }}
              trigger={["click"]}
            >
              <Button 
                type="primary"
                disabled={!isCompleted}>
                Télécharger en...
                <DownOutlined/>
              </Button>
            </Dropdown>
            <Button
              icon={isExpanded ? <MinusOutlined /> : <PlusOutlined />}
              type="primary"
              disabled={!isCompleted}
              onClick={() => toggleFollowUp(type)}
            />
          </Col>
        </Row>

        {isExpanded && isCompleted && (
          <div style={{ marginTop: 20 }}>
            <Row gutter={[24, 16]}>
              <Col span={8}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong style={{ fontSize: "14px", display: "block", marginBottom: 8 }}>Informations générales</Text>
                  {renderDetailRow(t("FollowUpCall:label_kinesiologist_name"), followUp.kinesologistName)}
                  {renderDetailRow(t("FollowUpCall:label_one_ten_scale_health"), followUp.selfHealthRating + "/10")}
                  {renderDetailRow(t("FollowUpCall:label_program_difficulty_level"), levelMap[followUp.programDifficultyLevel] || followUp.programDifficultyLevel)}
                </div>
              </Col>

              <Col span={8}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong style={{ fontSize: "14px", display: "block", marginBottom: 8 }}>Exercices</Text>
                  {renderDetailRow(t("FollowUpCall:label_average_prescribed_exercise"), formatFollowUpValue("freq", followUp.averagePrescribedExerciseDone))}
                  {renderDetailRow(t("FollowUpCall:label_prescription_practice_way"), formatFollowUpValue("way", followUp.exercisePrescriptionPracticeWay))}
                  {renderDetailRow(t("FollowUpCall:label_prescribed_exercice_opinion"), formatFollowUpValue("opinion", followUp.prescripedExerciseOpinion))}
                </div>
              </Col>

              <Col span={8}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong style={{ fontSize: "14px", display: "block", marginBottom: 8 }}>Satisfaction et douleur</Text>
                  {renderDetailRow(t("FollowUpCall:label_ap_satisfaction_level"), formatFollowUpValue("satis", followUp.apSatisfactionLevel))}
                  {renderDetailRow(t("FollowUpCall:label_program_pain_level"), formatFollowUpValue("pain", followUp.programPainLevel))}
                  {followUp.frequencyOfPain && renderDetailRow(t("FollowUpCall:label_frequency_of_pain"), formatFollowUpValue("freq", followUp.frequencyOfPain))}
                  {followUp.originOfPain && renderDetailRow(t("FollowUpCall:label_origin_of_pain"), formatFollowUpValue("origin", followUp.originOfPain))}
                </div>
              </Col>

              <Col span={8}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong style={{ fontSize: "14px", display: "block", marginBottom: 8 }}>Difficulté des exercices</Text>
                  {followUp.exercise1Difficulty && renderDetailRow("Exercice 1", formatFollowUpValue("diff", followUp.exercise1Difficulty))}
                  {followUp.exercise2Difficulty && renderDetailRow("Exercice 2", formatFollowUpValue("diff", followUp.exercise2Difficulty))}
                  {followUp.exercise3Difficulty && renderDetailRow("Exercice 3", formatFollowUpValue("diff", followUp.exercise3Difficulty))}
                  {followUp.exercise4Difficulty && renderDetailRow("Exercice 4", formatFollowUpValue("diff", followUp.exercise4Difficulty))}
                </div>
              </Col>

              <Col span={8}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong style={{ fontSize: "14px", display: "block", marginBottom: 8 }}>Sécurité et marche</Text>
                  {renderDetailRow(t("FollowUpCall:label_feeling_of_safety"), followUp.feelingOfSafety)}
                  {followUp.safetyFeelWhy && renderDetailRow(t("FollowUpCall:label_if_not_why"), followUp.safetyFeelWhy)}
                  {renderDetailRow(t("FollowUpCall:label_has_walked_past_weeks"), followUp.hasWalkedPastWeeks)}
                  {followUp.hasWalkedFrequency && renderDetailRow(t("FollowUpCall:label_has_walked_frequency"), formatFollowUpValue("freq", followUp.hasWalkedFrequency))}
                  {followUp.hasWalkedAverage && renderDetailRow(t("FollowUpCall:label_has_walked_average"), followUp.hasWalkedAverage + " min.")}
                </div>
              </Col>

              <Col span={8}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong style={{ fontSize: "14px", display: "block", marginBottom: 8 }}>Chute et motivation</Text>
                  {renderDetailRow(t("FollowUpCall:label_has_fallen_past_weeks"), followUp.hasFallenPastWeeks)}
                  {followUp.timesFallenCount && renderDetailRow(t("FollowUpCall:label_if_yes_how_much"), followUp.timesFallenCount)}
                  {followUp.fallCircumstances && renderDetailRow(t("FollowUpCall:label_if_yes_circumstances"), followUp.fallCircumstances)}
                  {renderDetailRow(t("FollowUpCall:label_prescribed_ap_motivation"), followUp.prescripedApMotivation)}
                  {followUp.notMotivatedReason && renderDetailRow(t("FollowUpCall:label_if_not_why"), followUp.notMotivatedReason)}
                </div>
              </Col>

              {followUp.otherActivityIntegration && (
                <Col span={8}>
                  <div style={{ marginBottom: 16 }}>
                    <Text strong style={{ fontSize: "14px", display: "block", marginBottom: 8 }}>{t("FollowUpCall:section_other_physical_activity")}</Text>
                    {renderDetailRow(t("FollowUpCall:label_if_yes_what"), followUp.additionalActivity1Name)}
                    {followUp.additionalActivity1Frequency && renderDetailRow(t("FollowUpCall:label_if_yes_what_frequency"), formatFollowUpValue("freq", followUp.additionalActivity1Frequency))}
                    {followUp.additionalActivity1Length && renderDetailRow(t("FollowUpCall:label_if_yes_what_length"), followUp.additionalActivity1Length + " min.")}
                    
                    {followUp.anotherActivityIntegration &&
                      <div>
                        <hr></hr>
                        {renderDetailRow(t("FollowUpCall:label_integrated_another_activity"), followUp.additionalActivity2Name)}
                        {followUp.additionalActivity2Frequency && renderDetailRow(t("FollowUpCall:label_if_yes_what_frequency"), formatFollowUpValue("freq", followUp.additionalActivity2Frequency))}
                        {followUp.additionalActivity2Length && renderDetailRow(t("FollowUpCall:label_if_yes_what_length"), followUp.additionalActivity2Length + " min.")}
                      </div>
                    }
                    {followUp.anotherActivityIntegration && followUp.yetAnotherActivityIntegration &&
                      <div>
                        <hr></hr>
                        {renderDetailRow(t("FollowUpCall:label_is_there_other"), followUp.additionalActivity3Name)}
                        {followUp.additionalActivity3Frequency && renderDetailRow(t("FollowUpCall:label_if_yes_what_frequency"), formatFollowUpValue("freq", followUp.additionalActivity3Frequency))}
                        {followUp.additionalActivity3Length && renderDetailRow(t("FollowUpCall:label_if_yes_what_length"), followUp.additionalActivity3Length + " min.")}
                      </div>
                    }
                  </div>
                </Col>
              )}

              <Col span={8}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong style={{ fontSize: "14px", display: "block", marginBottom: 8 }}>{t("FollowUpCall:section_medical_consultation")}</Text>
                  {renderDetailRow(t("FollowUpCall:label_has_seen_doctor"), followUp.hasSeenDoctor)}
                  {followUp.timesVisitedDoctorCount && renderDetailRow(t("FollowUpCall:label_if_yes_how_much"), followUp.timesVisitedDoctorCount)}
                  {followUp.doctorVisitReasons && renderDetailRow(t("FollowUpCall:label_if_yes_reason"), followUp.doctorVisitReasons)}
                  {renderDetailRow(t("FollowUpCall:label_hospital_day_surgery"), followUp.hasBeenToHospital)}
                  {followUp.timesVisitedHospitalCount && renderDetailRow(t("FollowUpCall:label_if_yes_how_much"), followUp.timesVisitedHospitalCount)}
                  {renderDetailRow(t("FollowUpCall:label_has_seen_other_professional"), followUp.hasSeenOtherProfessional)}
                  {followUp.timesVisitedSpecialistCount && renderDetailRow(t("FollowUpCall:label_if_yes_how_much"), followUp.timesVisitedSpecialistCount)}
                </div>
              </Col>
            </Row>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-6">
      <Card className="shadow-sm">
        <Row style={{ marginBottom: 20 }}>
          <Col span={8}>
            <Button
              type="primary"
              icon={<LeftOutlined />}
              onClick={handleReturn}
            >
              Retourner
            </Button>
          </Col>
          <Col span={8} style={{ textAlign: "center" }}>
            {patient && (
              <Title level={4} style={{ margin: 0 }}>
                {patient.firstname} {patient.lastname}
              </Title>
            )}
          </Col>
          <Col span={8}></Col>
        </Row>
      </Card>

      <Card className="shadow-sm" style={{ padding: "24px" }}>
        <Title level={3}>Évaluations effectuées</Title>

        {loadingEvaluations ? (
          <div style={{ textAlign: "center", padding: 50 }}>
            <Spin size="large" />
          </div>
        ) : errorMessageEvaluations ? (
          <Result
            status="error"
            title="Erreur de chargement"
            subTitle={errorMessageEvaluations}
            extra={
              <Button type="primary" onClick={fetchDataEvaluations}>
                Réessayer
              </Button>
            }
          />
        ) : evaluations.length === 0 ? (
          <Empty description="Aucune évaluation trouvée" />
        ) : (
          evaluations.map((evaluation, index) =>
            renderEvaluation(evaluation, index)
          )
        )}
      </Card>

      <Card className="shadow-sm" style={{ padding: "24px" }}>
        <Row justify="space-between">
          <Col span={18}>
            <Title level={3}>Historique Appel de suivi</Title>
          </Col>
          <Col span={6}>
            <Button 
              type="primary"
              disabled={loadingFollowUps || !hasCompletedFollowUps}
              onClick={() => exportAllFollowUpsPaceCsv(followUps, patientId)}
              >
              Télécharger tout en CSV
            </Button>
          </Col>
        </Row>

        {loadingFollowUps ? (
          <div style={{ textAlign: "center", padding: 50 }}>
            <Spin size="large" />
          </div>
        ) : errorMessageFollowUps ? (
          <Result
            status="error"
            title="Erreur de chargement"
            subTitle={errorMessageFollowUps}
            extra={
              <Button type="primary" onClick={fetchDataFollowUp}>
                Réessayer
              </Button>
            }
          />
        ) : (
          followUpTypes.map((type) => renderFollowUpRow(type))
        )}
      </Card>


    </div>
  );
}

export default EvaluationDisplay;

